import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Grid, 
  Switch, 
  FormControlLabel,
  Divider,
  IconButton,
  Avatar,
  CircularProgress
} from '@mui/material';
import { AddPhotoAlternate as AddPhotoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Organization, OrganizationFormData } from '../types/Organization';
import { useUser } from '../contexts/UserContext';

interface OrganizationFormProps {
  onSubmit: (formData: OrganizationFormData) => Promise<void>;
  initialData?: Partial<Organization>;
  loading?: boolean;
  formTitle?: string;
  submitButtonText?: string;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  onSubmit,
  initialData,
  loading = false,
  formTitle = 'Create Organization',
  submitButtonText = 'Create'
}) => {
  const { currentUser } = useUser();
  
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    website: initialData?.website || '',
    email: initialData?.email || (currentUser?.email || ''),
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    isPublic: initialData?.isPublic ?? true,
    social: initialData?.social || {}
  });
  
  const [logoPreview, setLogoPreview] = useState<string | null>(initialData?.logoUrl || null);
  const [coverPreview, setCoverPreview] = useState<string | null>(initialData?.coverPhotoUrl || null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    
    if (name === 'isPublic') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };
  
  const handleSocialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      social: {
        ...formData.social,
        [name]: value
      }
    });
  };
  
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCoverFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setCoverPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Organization description is required';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.website && !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Add file data if present
    const submissionData: OrganizationFormData = {
      ...formData
    };
    
    if (logoFile) {
      submissionData.logoImage = logoFile;
    }
    
    if (coverFile) {
      submissionData.coverImage = coverFile;
    }
    
    try {
      await onSubmit(submissionData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({
        form: 'There was an error submitting the form. Please try again.'
      });
    }
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 900, mx: 'auto', mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {formTitle}
      </Typography>
      
      <Divider sx={{ mb: 3 }} />
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Logo Upload */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Organization Logo
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2
              }}
            >
              {logoPreview ? (
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={logoPreview}
                    alt="Organization Logo"
                    sx={{ width: 100, height: 100, mb: 1 }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      backgroundColor: 'background.paper',
                      boxShadow: 1
                    }}
                    onClick={() => {
                      setLogoPreview(null);
                      setLogoFile(null);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AddPhotoIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                Recommended: Square image, at least 200x200px
              </Typography>
            </Box>
          </Grid>
          
          {/* Cover Photo Upload */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Cover Photo
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 2
              }}
            >
              {coverPreview ? (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={coverPreview}
                    alt="Organization Cover"
                    sx={{
                      width: '100%',
                      maxHeight: 120,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mb: 1
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      backgroundColor: 'background.paper',
                      boxShadow: 1
                    }}
                    onClick={() => {
                      setCoverPreview(null);
                      setCoverFile(null);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AddPhotoIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Cover
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleCoverChange}
                  />
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                Recommended: 1200x300px, 4:1 ratio
              </Typography>
            </Box>
          </Grid>
          
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Organization Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://example.com"
              error={!!errors.website}
              helperText={errors.website}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description}
              placeholder="Tell people what your organization is about"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+1 (123) 456-7890"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Office address or location"
            />
          </Grid>
          
          {/* Social Media Links */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Social Media Links
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Twitter"
              name="twitter"
              value={formData.social?.twitter || ''}
              onChange={handleSocialChange}
              placeholder="@username or URL"
              InputProps={{
                startAdornment: <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>@</Box>
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Facebook"
              name="facebook"
              value={formData.social?.facebook || ''}
              onChange={handleSocialChange}
              placeholder="Username or page URL"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Instagram"
              name="instagram"
              value={formData.social?.instagram || ''}
              onChange={handleSocialChange}
              placeholder="@username or URL"
              InputProps={{
                startAdornment: <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>@</Box>
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="LinkedIn"
              name="linkedin"
              value={formData.social?.linkedin || ''}
              onChange={handleSocialChange}
              placeholder="Company URL"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="YouTube"
              name="youtube"
              value={formData.social?.youtube || ''}
              onChange={handleSocialChange}
              placeholder="Channel URL"
            />
          </Grid>
          
          {/* Settings */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Settings
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublic}
                  onChange={handleInputChange}
                  name="isPublic"
                />
              }
              label="Public Organization (visible in search and directories)"
            />
          </Grid>
          
          {/* Form Errors */}
          {errors.form && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {errors.form}
              </Typography>
            </Grid>
          )}
          
          {/* Submit Button */}
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Submitting...' : submitButtonText}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default OrganizationForm; 